import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const FILTER_KEY = 'projectTypeFilter';

export default function useProjectDashboard({ user, userProfile, showToast }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [typeFilter, setTypeFilter] = useState(() => {
    try { return localStorage.getItem(FILTER_KEY) || 'all'; } catch { return 'all'; }
  });

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, typeFilter); } catch {}
  }, [typeFilter]);

  const loadProjects = useCallback(async () => {
    try {
      const list = isAdmin ? await api.getAllProjects() : await api.getMyProjects(user.uid);
      setProjects(list);
      return list;
    } catch (err) {
      showToast(err.message || 'Failed to load projects', 'error');
      return [];
    }
  }, [user.uid, isAdmin, showToast]);

  useEffect(() => {
    const init = async () => {
      const list = await loadProjects();

      if (list.length === 0 && userProfile?.role === 'admin') {
        setMigrating(true);
        try {
          const project = await api.migrateToProjects();
          if (project) {
            setProjects([project]);
            showToast('Migrated existing data to "Default Project"', 'success');
          }
        } catch {
          // No sprints to migrate
        }
        setMigrating(false);
      }

      setLoading(false);
    };
    init();
  }, [loadProjects, userProfile, showToast]);

  const handleCreateProject = async ({ name, description, type, members }) => {
    const project = await api.createProject({
      name,
      description,
      type,
      createdBy: user.uid,
    });
    for (const uid of members) {
      if (uid !== user.uid) {
        await api.addProjectMember(project.id, uid);
      }
    }
    await loadProjects();
    return project;
  };

  const handleDeleteProject = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteProject(deleteTarget);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget));
      showToast('Project deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete project', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filteredProjects = projects.filter((p) => typeFilter === 'all' || (p.type || 'scrum') === typeFilter);

  return {
    projects,
    filteredProjects,
    loading,
    migrating,
    deleteTarget,
    setDeleteTarget,
    typeFilter,
    setTypeFilter,
    isAdmin,
    handleCreateProject,
    handleDeleteProject,
  };
}
